<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { ONBOARDING_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import OnboardingShell from 'shared/components/onboarding/OnboardingShell.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import OnboardingSection from './shared/OnboardingSection.vue';
import InboxChannelsDialog from './inbox-setup/InboxChannelsDialog.vue';
import InboxChannelsFooter from './inbox-setup/InboxChannelsFooter.vue';
import ChannelRow from './inbox-setup/ChannelRow.vue';
import HelpCenterCreationStatus from './inbox-setup/HelpCenterCreationStatus.vue';
import { CHANNEL_TYPES } from 'dashboard/helper/inbox';
import { useChannelConnect } from './inbox-setup/useChannelConnect';
import { useDetectedChannels } from './inbox-setup/useDetectedChannels';
import { useChannelConfig } from './inbox-setup/useChannelConfig';
import { DIALOG_CHANNELS } from './inbox-setup/constants';
import {
  captureOnboardingChannelAction,
  captureOnboardingStepAttempted,
  captureOnboardingStepCompleted,
  captureOnboardingStepError,
  captureOnboardingStepViewed,
} from 'dashboard/helpers/onboardingAnalytics';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountId, currentAccount, finishOnboarding } = useAccount();
const { isEnterprise } = useConfig();
const { connectViaOAuth, connectWhatsapp } = useChannelConnect();
const { isConfigured } = useChannelConfig();

const helpCenterGenerationId = computed(
  () => currentAccount.value?.custom_attributes?.help_center_generation_id
);

const isSubmitting = ref(false);

const inboxes = useMapGetter('inboxes/getInboxes');
const globalConfig = useMapGetter('globalConfig/get');

const {
  displayedChannels,
  remainingChannels,
  connectedInbox,
  hasDetectedChannels,
} = useDetectedChannels();

const channelsDialogRef = ref(null);

onMounted(() => {
  useTrack(ONBOARDING_EVENTS.INBOX_SETUP_VISITED);
  captureOnboardingStepViewed('inbox_setup');
  store.dispatch('inboxes/get');
});

const completeOnboarding = async (event, destination, channelAction) => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  captureOnboardingStepAttempted('inbox_setup');
  try {
    // Declare the step we're completing so the controller only clears it when
    // the stored step still matches (idempotent). setUser then refreshes the
    // auth store so the router guard sees the cleared step and lets us in.
    await finishOnboarding({ onboarding_step: 'inbox_setup' });
    useTrack(event);
    if (channelAction) {
      await captureOnboardingChannelAction(
        channelAction.channelType,
        channelAction.action
      );
    }
    await captureOnboardingStepCompleted('inbox_setup');
    await store.dispatch('setUser');
    router.push(
      destination || { name: 'home', params: { accountId: accountId.value } }
    );
  } catch {
    useAlert(t('ONBOARDING_INBOX_SETUP.ERROR'));
    captureOnboardingStepError('inbox_setup', 'api');
  } finally {
    isSubmitting.value = false;
  }
};

const handleContinue = () =>
  completeOnboarding(ONBOARDING_EVENTS.INBOX_SETUP_COMPLETED, undefined, {
    channelType: 'existing',
    action: 'continued',
  });
const handleSkip = () =>
  completeOnboarding(ONBOARDING_EVENTS.INBOX_SETUP_SKIPPED, undefined, {
    channelType: 'none',
    action: 'skipped',
  });
const openChannelSetup = channel =>
  completeOnboarding(ONBOARDING_EVENTS.INBOX_SETUP_COMPLETED, {
    name: 'settings_inboxes_page_channel',
    params: { accountId: accountId.value, sub_page: channel },
    query:
      channel === CHANNEL_TYPES.WHATSAPP ? { provider: 'whatsapp_manual' } : {},
  });
const openChannelsDialog = () => channelsDialogRef.value?.open();
const refetchInboxes = () => store.dispatch('inboxes/get');

// WhatsApp connects via Meta's embedded-signup popup; the DIALOG_CHANNELS open
// the channels dialog preselected to their in-dialog step; the rest go through
// the redirect OAuth flow (Gmail/Outlook keyed by email provider, Instagram by
// channel type).
const connectChannel = channel => {
  captureOnboardingChannelAction(channel.type, 'selected');
  if (
    channel.settingsSetup ||
    (channel.type === CHANNEL_TYPES.WHATSAPP && !isConfigured(channel.type))
  ) {
    openChannelSetup(channel.type);
    return;
  }
  if (channel.type === CHANNEL_TYPES.WHATSAPP) {
    connectWhatsapp();
    return;
  }
  if (DIALOG_CHANNELS.includes(channel.type)) {
    channelsDialogRef.value?.open(channel.type);
    return;
  }
  connectViaOAuth(channel.inbox?.provider || channel.type);
};
</script>

<template>
  <div>
    <OnboardingShell
      :step="5"
      :logo="globalConfig.logo"
      :installation-name="globalConfig.installationName"
      authenticated
    >
      <div
        class="mb-5 flex size-12 items-center justify-center rounded-2xl bg-woot-50 text-woot-800 ring-1 ring-woot-100"
      >
        <Icon icon="i-lucide-check" class="size-6" />
      </div>
      <h1
        class="mb-3 text-[30px] font-semibold leading-[1.15] tracking-[-0.035em] text-slate-900 sm:text-[34px]"
      >
        {{
          currentAccount?.trial?.status === 'active'
            ? t('ONBOARDING_NEXT.GUIDED.TRIAL_ACTIVE')
            : t('ONBOARDING_INBOX_SETUP.GREETING')
        }}
      </h1>
      <p class="mb-6 text-[15px] leading-relaxed text-slate-600">
        {{ t('ONBOARDING_INBOX_SETUP.SUBTITLE') }}
      </p>
      <OnboardingSection
        v-if="isEnterprise && helpCenterGenerationId"
        :title="t('ONBOARDING_INBOX_SETUP.CREATED_FOR_YOU.TITLE')"
        icon="i-lucide-sparkles"
      >
        <div class="divide-y divide-n-weak">
          <HelpCenterCreationStatus />
        </div>
      </OnboardingSection>

      <OnboardingSection
        :title="t('ONBOARDING_INBOX_SETUP.CHANNELS.TITLE')"
        icon="i-lucide-inbox"
      >
        <div
          v-if="hasDetectedChannels"
          class="flex items-center gap-2 p-3 border-b border-dashed border-n-strong"
        >
          <Icon
            icon="i-lucide-lightbulb"
            class="size-4 text-n-slate-11 flex-shrink-0"
          />
          <span class="flex-1 min-w-0 text-body-main text-n-slate-11">
            {{ t('ONBOARDING_INBOX_SETUP.CHANNELS.HEADER') }}
          </span>
        </div>
        <ChannelRow
          v-for="channel in displayedChannels"
          :key="channel.type"
          :channel="channel"
          :connected-inbox="connectedInbox(channel)"
          @connect="connectChannel"
        />
        <InboxChannelsFooter
          :remaining-channels="remainingChannels"
          @view-all="openChannelsDialog"
        />
      </OnboardingSection>
      <div class="mt-6 flex flex-col gap-3">
        <NextButton
          v-if="inboxes.length"
          type="button"
          class="!min-h-12 !rounded-xl !bg-woot-500 !text-woot-900"
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
          @click="handleContinue"
        >
          {{ t('ONBOARDING_INBOX_SETUP.CONTINUE') }}
        </NextButton>
        <NextButton
          type="button"
          class="!min-h-12 !rounded-xl !bg-white !text-slate-600"
          ghost
          :disabled="isSubmitting"
          @click="handleSkip"
        >
          {{ t('ONBOARDING_INBOX_SETUP.SKIP') }}
        </NextButton>
      </div>
    </OnboardingShell>
    <InboxChannelsDialog
      ref="channelsDialogRef"
      :inboxes="inboxes"
      @connected="refetchInboxes"
      @setup="openChannelSetup"
    />
  </div>
</template>
